// Local preview store: notes live in this browser's localStorage,
// media inlined as data URLs. Lets the board be felt with no backend.

import type { Note, NoteUpdate } from "@ts/core/types.ts";
import type { MediaChange, NewNote, Store } from "@ts/store/store.ts";
import { uuid } from "@ts/core/uuid.ts";
import { blobToDataUrl } from "../util/dom.ts";

const KEY = "memorybox-notes";
const EMAIL_KEY = "memorybox-emails";

const load = (): Note[] => JSON.parse(localStorage.getItem(KEY) || "[]");
const save = (notes: Note[]) => localStorage.setItem(KEY, JSON.stringify(notes));

export class LocalStore implements Store {
  readonly remote = false;

  async init(): Promise<Date | null> {
    return null;
  }

  async list(): Promise<Note[]> {
    return load();
  }

  async create(note: NewNote, media: MediaChange) {
    const full: Note = {
      ...note,
      id: uuid(),
      created_at: new Date().toISOString(),
      photo_url: media.photo ? await blobToDataUrl(media.photo) : null,
      audio_url: media.audio ? await blobToDataUrl(media.audio) : null,
    };
    const all = load();
    all.push(full);
    save(all);
    return { note: full, token: null };
  }

  async update(id: string, _token: string | null, fields: NoteUpdate, media: MediaChange) {
    const patch: Partial<Note> = { ...fields };
    if (fields.name !== undefined) patch.name = fields.name || null;
    if (media.photo) patch.photo_url = await blobToDataUrl(media.photo);
    else if (media.removePhoto) patch.photo_url = null;
    if (media.audio) patch.audio_url = await blobToDataUrl(media.audio);
    else if (media.removeAudio) patch.audio_url = null;

    const all = load();
    const n = all.find((n) => n.id === id);
    if (n) Object.assign(n, patch);
    save(all);
    return patch;
  }

  async remove(id: string) {
    save(load().filter((n) => n.id !== id));
  }

  async saveEmail(email: string) {
    const all: string[] = JSON.parse(localStorage.getItem(EMAIL_KEY) || "[]");
    all.push(email);
    localStorage.setItem(EMAIL_KEY, JSON.stringify(all));
  }

  subscribe(_onEvent: unknown, onPresence?: (count: number) => void) {
    onPresence?.(1); // just you in local preview mode
  }

  track(_visitor: string, name: string, props?: Record<string, unknown>) {
    console.debug("[analytics:preview]", name, props ?? {});
  }
}
